import { INote, INoteTemplate } from '@dataTypes/note.type'
import type { INoteTranscriptSegment } from '@dataTypes/note.type'
import axios from './axiosConfig'
import { getEnv } from '@utils/env'
import { logger } from '@utils/safeLogger'
import { toApiError } from './errors/api-error'

// Constants
const BUSINESS_LAYER_API = getEnv('REACT_APP_BUSINESS_LAYER_NOTE', '')

const headers = {
  'Content-Type': 'application/json',
}

// Types
interface ApiResponse<T> {
  statusCode: number
  message: string
  data: T
}

type NoteResponseData = {
  note: INote
}

type NotesResponseData = {
  notes: INote[]
}

type NoteTemplatesResponseData = {
  templates: INoteTemplate[]
}

type NoteTemplateResponseData = {
  template: INoteTemplate
}

type GenericResponseData = Record<string, unknown>

export type GetAllNotesResponse = ApiResponse<NotesResponseData>
export type GetNoteByIdResponse = ApiResponse<NoteResponseData>
export type CreateNoteResponse = ApiResponse<NoteResponseData>
export type UpdateNoteResponse = ApiResponse<NoteResponseData>
export type DeleteNoteResponse = ApiResponse<GenericResponseData>
export type BulkDeleteNotesResponse = ApiResponse<GenericResponseData>
export type GetNoteTemplatesResponse = ApiResponse<NoteTemplatesResponseData>
export type CreateNoteTemplateResponse = ApiResponse<NoteTemplateResponseData>
export type UpdateNoteTemplateResponse = ApiResponse<NoteTemplateResponseData>
export type DeleteNoteTemplateResponse = ApiResponse<GenericResponseData>

// API Functions
export type GetAllNotesParams = {
  contactId?: string
  patientId?: string
  contextType?: string
  contextId?: string
  latestOnly?: boolean
}

export const getAllNotes = async (
  params?: GetAllNotesParams,
): Promise<GetAllNotesResponse> => {
  try {
    const contactId = params?.contactId
    const patientId = params?.patientId
    const contextType = params?.contextType
    const contextId = params?.contextId
    const latestOnly = params?.latestOnly

    if (!contactId && !patientId && !contextId && !contextType) {
      throw toApiError(
        new Error(
          'At least one of contact_id, patient_id, context_id, or context_type is required.',
        ),
        'At least one of contact_id, patient_id, context_id, or context_type is required.',
      )
    }

    const queryParams: Record<string, string> = {}
    if (contactId) queryParams.contact_id = contactId
    if (patientId) queryParams.patient_id = patientId
    if (contextType) queryParams.context_type = contextType
    if (contextId) queryParams.context_id = contextId
    if (typeof latestOnly === 'boolean') {
      queryParams.latest_only = String(latestOnly)
    }

    const response = await axios.get<GetAllNotesResponse>(
      `${BUSINESS_LAYER_API}/api/v1/notes`,
      {
        params: queryParams,
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in getAllNotes:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const getNoteById = async (
  noteId: string,
): Promise<GetNoteByIdResponse> => {
  try {
    const response = await axios.get<GetNoteByIdResponse>(
      `${BUSINESS_LAYER_API}/api/v1/notes/${noteId}`,
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in getNoteById:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

// Note image upload (for rich text body): get upload URL then client uploads to Azure
export type NoteUploadUrlPayload = {
  contentType: string
  filename: string
  prefix?: string
}

export type NoteUploadUrlResult = {
  upload_url: string
  blob_url: string
  blob_name: string
}

export const getNoteUploadUrl = async (
  payload: NoteUploadUrlPayload,
): Promise<ApiResponse<NoteUploadUrlResult>> => {
  try {
    const response = await axios.post<ApiResponse<NoteUploadUrlResult>>(
      `${BUSINESS_LAYER_API}/api/v1/notes/upload-url`,
      payload,
      { headers },
    )
    return response.data
  } catch (e: unknown) {
    logger.error('Error in getNoteUploadUrl:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const getNoteReadOnlyUrl = async (
  blobName: string,
  expirationHours?: number,
): Promise<ApiResponse<{ read_only_url: string }>> => {
  try {
    const response = await axios.post<ApiResponse<{ read_only_url: string }>>(
      `${BUSINESS_LAYER_API}/api/v1/notes/read-only-url`,
      { blobName, expirationHours },
      { headers },
    )
    return response.data
  } catch (e: unknown) {
    logger.error('Error in getNoteReadOnlyUrl:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

/**
 * Resolves img tags that have data-blob but no valid src (missing, empty, or blob:).
 * Fetches read-only URL for each and sets src so images display and persist correctly.
 * Uses both DOM and regex so we catch imgs even if the parser normalizes attributes.
 */
export const resolveNoteBodyBlobSrc = async (body: string): Promise<string> => {
  if (!body || typeof body !== 'string') return body
  if (!body.includes('data-blob')) return body

  // Collect blob names that need a src: match <img ... data-blob="name" ...> where tag has no http(s) src
  const blobNamesToResolve: string[] = []
  const imgTagRegex = /<img\s[^>]*?data-blob=["']([^"']+)["'][^>]*>/gi
  let match: RegExpExecArray | null
  while ((match = imgTagRegex.exec(body)) !== null) {
    const fullTag = match[0]
    const blobName = match[1]
    const hasValidSrc = /src=["']https?:\/\//i.test(fullTag)
    if (!hasValidSrc && blobName) {
      blobNamesToResolve.push(blobName)
    }
  }
  if (blobNamesToResolve.length === 0) return body

  // Fetch read-only URL for each blob (dedupe by blob name)
  const urlByBlob = new Map<string, string>()
  const uniqueBlobNames = Array.from(new Set(blobNamesToResolve))
  for (let i = 0; i < uniqueBlobNames.length; i++) {
    const blobName = uniqueBlobNames[i]
    try {
      const res = await getNoteReadOnlyUrl(blobName)
      if (res.statusCode === 200 && res.data?.read_only_url) {
        urlByBlob.set(blobName, res.data.read_only_url)
      }
    } catch {
      // Resolve failed for this blob; skip
    }
  }
  if (urlByBlob.size === 0) return body

  // Replace each img that has data-blob but no valid src: insert src="url" after <img
  let result = body
  const insertSrcRegex = /<img(\s[^>]*?)data-blob=["']([^"']+)["']([^>]*)>/gi
  result = result.replace(insertSrcRegex, (_, before, blobName, after) => {
    const url = urlByBlob.get(blobName)
    if (!url) return `<img${before}data-blob="${blobName}"${after}>`
    return `<img src="${url}"${before}data-blob="${blobName}"${after}>`
  })
  return result
}

/**
 * Replaces img tags with blob: URLs by uploading each to Azure and setting data-blob + read-only src.
 * Blob URLs are browser-only; the server cannot fetch them, so we must upload from the client before save.
 */
async function uploadBlobUrlsInBody(body: string): Promise<string> {
  if (!body || typeof body !== 'string' || !body.includes('blob:')) return body
  const div = document.createElement('div')
  div.innerHTML = body
  const imgs = div.querySelectorAll<HTMLImageElement>('img[src^="blob:"]')
  if (imgs.length === 0) return body

  for (let i = 0; i < imgs.length; i++) {
    const img = imgs[i]
    const blobUrl = img.getAttribute('src')
    if (!blobUrl || !blobUrl.startsWith('blob:')) continue
    try {
      const res = await fetch(blobUrl)
      const blob = await res.blob()
      const contentType = blob.type || 'image/png'
      const filename = `${Date.now()}-${i}.${contentType.split('/')[1] || 'png'}`
      const uploadUrlRes = await getNoteUploadUrl({
        contentType,
        filename,
      })
      const hasUploadUrl =
        uploadUrlRes.statusCode === 200 && uploadUrlRes.data?.upload_url
      if (!hasUploadUrl) continue
      const { upload_url, blob_name } = uploadUrlRes.data
      const putRes = await fetch(upload_url, {
        method: 'PUT',
        body: blob,
        headers: {
          'Content-Type': contentType,
          'x-ms-blob-type': 'BlockBlob',
        },
      })
      if (!putRes.ok) continue
      const readOnlyRes = await getNoteReadOnlyUrl(blob_name)
      const hasReadOnlyUrl =
        readOnlyRes.statusCode === 200 && readOnlyRes.data?.read_only_url
      if (!hasReadOnlyUrl) continue
      img.setAttribute('data-blob', blob_name)
      img.setAttribute('src', readOnlyRes.data.read_only_url)
    } catch (e) {
      logger.warn('Failed to upload blob URL image:', e)
    }
  }
  return div.innerHTML
}

export const createNote = async (
  noteData: Partial<INote>,
): Promise<CreateNoteResponse> => {
  try {
    const payload = { ...noteData }
    if (typeof payload.body === 'string') {
      if (payload.body.includes('blob:')) {
        payload.body = await uploadBlobUrlsInBody(payload.body)
      }
      if (payload.body.includes('data-blob')) {
        payload.body = await resolveNoteBodyBlobSrc(payload.body)
      }
    }
    const response = await axios.post<CreateNoteResponse>(
      `${BUSINESS_LAYER_API}/api/v1/notes`,
      payload,
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in createNote:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const updateNote = async (
  noteId: string,
  updateData: Partial<INote>,
): Promise<UpdateNoteResponse> => {
  try {
    const payload = { ...updateData }
    if (typeof payload.body === 'string') {
      if (payload.body.includes('blob:')) {
        payload.body = await uploadBlobUrlsInBody(payload.body)
      }
      if (payload.body.includes('data-blob')) {
        payload.body = await resolveNoteBodyBlobSrc(payload.body)
      }
    }
    const response = await axios.put<UpdateNoteResponse>(
      `${BUSINESS_LAYER_API}/api/v1/notes`,
      {
        note_id: noteId,
        ...payload,
      },
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in updateNote:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const deleteNoteById = async (
  noteId: string,
): Promise<DeleteNoteResponse> => {
  try {
    const response = await axios.delete<DeleteNoteResponse>(
      `${BUSINESS_LAYER_API}/api/v1/notes/${noteId}`,
      {
        headers,
      },
    )

    if (response.data.statusCode !== 200) {
      throw new Error(response.data.message)
    }

    return response.data
  } catch (e: unknown) {
    logger.error('Error in deleteNoteById:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const bulkDeleteNotes = async (
  noteIds: string[],
): Promise<BulkDeleteNotesResponse> => {
  try {
    const response = await axios.delete<BulkDeleteNotesResponse>(
      `${BUSINESS_LAYER_API}/api/v1/notes/bulk`,
      {
        data: {
          note_ids: noteIds,
        },
        headers,
      },
    )

    if (response.data.statusCode !== 200) {
      throw new Error(response.data.message)
    }

    return response.data
  } catch (e: unknown) {
    logger.error('Error in bulkDeleteNotes:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

// Note templates (clinic-scoped)
export const getNoteTemplates = async (): Promise<GetNoteTemplatesResponse> => {
  try {
    const response = await axios.get<GetNoteTemplatesResponse>(
      `${BUSINESS_LAYER_API}/api/v1/notes/templates`,
      { headers },
    )
    return response.data
  } catch (e: unknown) {
    logger.error('Error in getNoteTemplates:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const createNoteTemplate = async (payload: {
  name: string
  content: string
  category?: string
}): Promise<CreateNoteTemplateResponse> => {
  try {
    const response = await axios.post<CreateNoteTemplateResponse>(
      `${BUSINESS_LAYER_API}/api/v1/notes/templates`,
      payload,
      { headers },
    )
    return response.data
  } catch (e: unknown) {
    logger.error('Error in createNoteTemplate:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const updateNoteTemplate = async (
  templateId: string,
  payload: { name?: string; content?: string; category?: string | null },
): Promise<UpdateNoteTemplateResponse> => {
  try {
    const response = await axios.put<UpdateNoteTemplateResponse>(
      `${BUSINESS_LAYER_API}/api/v1/notes/templates/${templateId}`,
      payload,
      { headers },
    )
    return response.data
  } catch (e: unknown) {
    logger.error('Error in updateNoteTemplate:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const deleteNoteTemplate = async (
  templateId: string,
): Promise<DeleteNoteTemplateResponse> => {
  try {
    const response = await axios.delete<DeleteNoteTemplateResponse>(
      `${BUSINESS_LAYER_API}/api/v1/notes/templates/${templateId}`,
      { headers },
    )
    if (response.data.statusCode !== 200) {
      throw new Error(response.data.message)
    }
    return response.data
  } catch (e: unknown) {
    logger.error('Error in deleteNoteTemplate:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export type NotesTranscribeResponseData = {
  segments: INoteTranscriptSegment[]
  speaker_map?: Record<string, string>
  usage?: Record<string, unknown>
}

export type NotesTranscribeResponse = ApiResponse<NotesTranscribeResponseData>

export type TranscribeNoteAudioParams = {
  audioBlob: Blob
  filename?: string
  doctorName?: string
  patientName?: string
  patientId?: string
}

/** Batch diarize remap (Doctor/Patient). Used once on Smart-mode stop. */
export const transcribeNoteAudio = async (
  params: TranscribeNoteAudioParams,
): Promise<NotesTranscribeResponse> => {
  try {
    const formData = new FormData()
    formData.append(
      'file',
      params.audioBlob,
      params.filename || 'notes-recording.webm',
    )
    if (params.doctorName?.trim()) {
      formData.append('doctor_name', params.doctorName.trim())
    }
    if (params.patientName?.trim()) {
      formData.append('patient_name', params.patientName.trim())
    }
    if (params.patientId?.trim()) {
      formData.append('patient_id', params.patientId.trim())
    }

    const response = await axios.post<NotesTranscribeResponse>(
      `${BUSINESS_LAYER_API}/api/v1/notes/transcribe`,
      formData,
      {
        headers: {
          'Content-Type': false as unknown as string,
        },
        timeout: 120_000,
      },
    )
    return response.data
  } catch (e: unknown) {
    logger.error('Error in transcribeNoteAudio:', e)
    throw toApiError(e, 'Failed to transcribe note audio')
  }
}
